import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { MapContext } from "contexts/MapContext";
import { UserContext } from "contexts/UserContext";
import { loadModules } from "esri-loader";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import CardMedia from "@material-ui/core/CardMedia";
import StartingImageIcon from "assets/icons/trailcard/triphistory/StartingImageIcon.png";
import EndingImageIcon from "assets/icons/trailcard/triphistory/EndingImageIcon.png";
import NoImage from "assets/icons/trailcard/triphistory/NoTripImage.png";

const useStyles = makeStyles({
  root: {
    borderRadius: "20px",
    margin: "1vh 1vw",
    boxShadow: "0px 1px 5px rgba(0, 0, 0, 0.2)",
  },
  media: {
    height: 150,
    background: "center",
    backgroundSize: "cover",
    backgroundBlendMode: "screen",
    filter: "brightness(70%)",
  },
  overlayTrail: {
    width: "26vw",
    color: "white",
    fontFamily: "Montserrat",
    fontWeight: "bold",
    fontSize: "20px",
    lineHeight: "24px",
    letterSpacing: "0.25px",
    overflow: "hidden",
    whiteSpace: "break-spaces",
  },
  overlayManageArea: {
    color: "white",
    fontFamily: "Open Sans",
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.25px",
  },
  infoFlex: {
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    top: "85px",
    left: "10px",
  },
  flexBox: {
    display: "flex",
    flexDirection: "row",
    fontFamily: "Open Sans",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.25px",
    color: "#4D4D4D",
  },
  distance: {
    fontWeight: "600",
    lineHeight: "20px",
    fontSize: "14px",
    letterSpacing: "0.25px",
    /* identical to box height, or 143% */
  },
  trailConfiguration: {
    fontWeight: "300",
    lineHeight: "20px",
    fontSize: "14px",
    letterSpacing: "0.25px",
  },
  difficulty: {
    fontFamily: "Open Sans",
    background: "#D1E9CB",
    borderRadius: "16px",
    width: "54px",
    height: "24px",
    textAlign: "center",
    marginLeft: "15px",
    fontFamily: "Open Sans",
    fontStyle: "normal",
    fontWeight: "600",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.25px",
    color: "#222222",
  },
  fee: {
    fontWeight: "600",
    marginLeft: "auto",
    marginRight: "10px",
  },
  iconFlex: {
    display: "flex",
    flexDirection: "row",
    marginTop: "8px",
    fontFamily: "Open Sans",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.25px",
    color: "#4D4D4D",
    gap: "5px",
  },
  favIcon: {
    zIndex: "2",
    backgroundSize: "contain",
    color: "white",
    height: "18.5px",
    width: "20px",
    top: "13px",
    left: "13px",
    position: "absolute",
  },
  imgFlex: {
    display: "flex",
  },
  trailType: {
    color: "#4D4D4D",
    fontFamily: "Open Sans",
    fontWeight: "600",
    fontSize: "16px",
    lineHeight: "19.2px",
    letterSpacing: "0.25px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    justifySelf: "flex-start",
    flexGrow: "1",
    display: "inline-flex",
  },
  difficultyChip: {
    justifySelf: "flex-end",
  },
  managerFlex: {
    display: "flex",
    flexDirection: "column",
    fontFamily: "Open Sans",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.25px",
    color: "#4D4D4D",
    marginTop: "8px",
  },
  managerAttr: {
    color: "#4D4D4D",
    fontFamily: "Open Sans",
    fontWeight: "600",
    fontSize: "14px",
    lineHeight: "16.8px",
    letterSpacing: "0.25px",
    marginTop: "4px",
  },
  managerTitle: {
    color: "#4D4D4D",
    fontFamily: "Open Sans",
    fontWeight: "300",
    fontSize: "14px",
    lineHeight: "16.8px",
    letterSpacing: "0.25px",
  },
  distanceSurfaceFlex: {
    display: "flex",
    flexDirection: "row",
    fontFamily: "Open Sans",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.25px",
    color: "#4D4D4D",
    marginTop: "8px",
    justifyContent: "space-between",
  },
});

export default function TripHistoryCard({
  feature,
  attributes,
  geometry,
  loggedIn,
}) {
  const classes = useStyles();
  const { mapView, setSidebarSelection } = useContext(MapContext);
  const { userInfo } = useContext(UserContext);
  const [trailImage, setTrailImage] = useState(null);
  const [coverImage, setCoverImage] = useState(null);

  useEffect(() => {
    (async () => {
      const [AttachmentQuery, QueryTask] = await loadModules([
        "esri/tasks/support/AttachmentQuery",
        "esri/tasks/QueryTask",
      ]);
      const queryTask = new QueryTask({
        url: userInfo.tripHistoryUrl,
      });

      const query = new AttachmentQuery({
        where: `OBJECTID = ${attributes.OBJECTID}`, // query all features in the layer
        attachmentsWhere: "1=1", // attachment queries
      });
      // When resolved, returns a count of the features that satisfy the query.
      queryTask.executeAttachmentQuery(query).then(function (results) {
        if (results && results[attributes.OBJECTID]) {
          setTrailImage(results[attributes.OBJECTID][0].url);
          setCoverImage(
            results[attributes.OBJECTID][
              results[attributes.OBJECTID].length - 1
            ].url
          );
        }
      });
    })();
  }, []);

  const handleOnClick = async () => {
    const {
      addLineGraphicTripHistory,
      getAllTrailsByIds,
      addMarkersTripHistory,
    } = await import("data/map");
    //Get all trails by trails hiked ids and pass it into trail details

    let nearbyFeatures = [];
    const trailsHiked = JSON.parse(attributes.TrailsHiked);
    if (trailsHiked && Object.keys(trailsHiked)) {
      nearbyFeatures = await getAllTrailsByIds(
        trailsHiked.map((trail) => trail.neighborTrailId)
      );
    }
    attributes.TrailImage = coverImage;
    setSidebarSelection([
      "Recorded Trip Details",
      feature,
      attributes,
      nearbyFeatures,
    ]);
    const polyline = await addLineGraphicTripHistory(feature, mapView);

    await addMarkersTripHistory(polyline, StartingImageIcon, EndingImageIcon);
  };

  const getDateTime = () => {
    return new Date(attributes.StartDateTime).toLocaleString("default", {
      month: "long",
      year: "numeric",
      day: "numeric",
    });
  };

  return (
    <Card className={classes.root} onClick={handleOnClick}>
      <CardActionArea>
        <CardMedia
          className={classes.media}
          image={trailImage ? trailImage : NoImage}
          title="Trail History Card"
        />
        <div class={classes.imgFlex}></div>
        <div class={classes.infoFlex}>
          <div class={classes.overlayTrail}>{attributes.TripName}</div>
          <div class={classes.overlayManageArea}>{getDateTime()}</div>
        </div>
      </CardActionArea>
    </Card>
  );
}
